import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Pencil, Sliders, Lock } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";

import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import {
  useDefaultWeightsPreset,
  useUpdateWeightsPreset,
} from "@/application/use-cases/products/use-weights.queries";
import {
  effectiveWeights,
  setAndRenormalize,
  sumWeights,
} from "@/application/use-cases/products/normalize-weights";
import { CreateCriterionDialog } from "@/presentation/components/criteria/create-criterion-dialog";
import { EditCriterionDialog } from "@/presentation/components/criteria/edit-criterion-dialog";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  CRITERION_GROUP_LABEL,
  CRITERION_GROUP_ORDER,
  colorForGroup,
} from "@/presentation/data/criteria";
import type {
  CriterionGroup,
  EvaluationCriterionRow,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/evaluation-criteria/")({
  component: EvaluationCriteriaPage,
});

function EvaluationCriteriaPage() {
  const criteriaQ = useEvaluationCriteria();
  const presetQ = useDefaultWeightsPreset();
  const updatePreset = useUpdateWeightsPreset();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<EvaluationCriterionRow | null>(null);

  const criteria = criteriaQ.data ?? [];
  const activePreset = presetQ.data ?? null;

  // The weight that actually scores is the active preset's value (falling back
  // to each criterion's baseline). Editing here writes the active preset.
  const effective = useMemo(
    () => effectiveWeights(criteria, activePreset?.weights ?? {}),
    [criteria, activePreset]
  );
  const weightSum = sumWeights(effective);
  const derivedCount = criteria.filter((c) => c.derivation_kind === "derived-coverage").length;
  const dealbreakerCount = criteria.filter((c) => c.is_dealbreaker_eligible).length;

  const handleSetWeight = (slug: string, value: number) => {
    if (!activePreset) return;
    const next = setAndRenormalize(effective, slug, value);
    updatePreset.mutate({ id: activePreset.id, patch: { weights: next } });
  };

  const grouped = groupCriteria(criteria);

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="evaluation-criteria" />
      </div>
      <RouteHero
        eyebrow="Configuration"
        icon={Sliders}
        numeral={String(criteria.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix="criteria"
        title="The weighted axes every product is scored against."
        summary="Adding or removing criteria affects all products simultaneously. Weights shown are the active preset's — editing a weight writes that preset and rebalances the rest to 100%. Derived-coverage criteria compute from the feature × product matrix; manual ones are scored on each product's detail page."
        meta={[
          {
            label: "Active preset",
            value: activePreset?.name ?? "—",
            tone: "cyan",
          },
          {
            label: "Weight sum",
            value: `${(weightSum * 100).toFixed(0)}%`,
            tone: Math.abs(weightSum - 1) < 0.005 ? "green" : "amber",
          },
          { label: "Auto-derived", value: derivedCount, tone: "cyan" },
          { label: "Dealbreaker-elig.", value: dealbreakerCount },
          { label: "Total", value: criteria.length },
        ]}
        actions={
          <Button
            onClick={() => setCreateOpen(true)}
            className="inline-flex w-fit items-center gap-1.5 print:hidden"
          >
            <Plus className="size-3.5" />
            New criterion
          </Button>
        }
      />

      <div className="flex flex-col gap-7">
        {CRITERION_GROUP_ORDER.map((group) => {
          const rows = grouped.get(group);
          if (!rows || rows.length === 0) return null;
          return (
            <section key={group} className="flex flex-col gap-3">
              <h2 className="eyebrow flex items-center gap-2">
                <span
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: colorForGroup(group) }}
                />
                {CRITERION_GROUP_LABEL[group]}
              </h2>
              <div className="glow-card divide-border/60 divide-y overflow-hidden">
                <TableHeader />
                {rows.map((c) => (
                  <Row
                    key={c.id}
                    criterion={c}
                    accent={colorForGroup(c.group_key)}
                    weight={effective[c.slug] ?? c.weight_default}
                    canEditWeight={activePreset !== null}
                    onSetWeight={(v) => handleSetWeight(c.slug, v)}
                    onEdit={() => setEditing(c)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <CreateCriterionDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditCriterionDialog criterion={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function TableHeader() {
  return (
    <div className="text-muted-foreground bg-muted/30 grid grid-cols-[1.4fr_180px_80px_120px_60px] items-center gap-3 px-4 py-2 text-[10.5px] font-medium tracking-[0.12em] uppercase">
      <span>Criterion</span>
      <span>Slug</span>
      <span className="text-right">Weight</span>
      <span>Derivation</span>
      <span></span>
    </div>
  );
}

function Row({
  criterion,
  accent,
  weight,
  canEditWeight,
  onSetWeight,
  onEdit,
}: {
  criterion: EvaluationCriterionRow;
  accent: string;
  weight: number;
  canEditWeight: boolean;
  onSetWeight: (value: number) => void;
  onEdit: () => void;
}) {
  const isDerived = criterion.derivation_kind === "derived-coverage";
  return (
    <div className="grid grid-cols-[1.4fr_180px_80px_120px_60px] items-center gap-3 px-4 py-3 print:break-inside-avoid">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span
            className="inline-block size-1.5 shrink-0 rounded-full"
            style={{ background: accent }}
          />
          <span className="text-[13px] leading-tight font-medium">{criterion.name}</span>
        </div>
        {criterion.description ? (
          <span className="text-foreground/60 line-clamp-1 pl-3.5 text-[11.5px] leading-snug print:line-clamp-none">
            {criterion.description}
          </span>
        ) : null}
      </div>
      <span className="font-data text-foreground/60 text-[11px]">{criterion.slug}</span>
      <WeightCell
        value={weight}
        baseline={criterion.weight_default}
        disabled={!canEditWeight}
        onCommit={onSetWeight}
      />
      <div>
        {isDerived ? (
          <span
            className="font-data inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-medium tracking-[0.10em] uppercase"
            style={{
              background: "oklch(0.95 0.005 240)",
              color: "oklch(0.45 0.01 240)",
            }}
          >
            <Lock className="size-2.5" />
            auto
          </span>
        ) : (
          <span className="font-data text-foreground/55 text-[10.5px] tracking-[0.10em] uppercase">
            manual
          </span>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onEdit}
        aria-label="Edit criterion"
        className="print:hidden"
      >
        <Pencil className="size-3.5" />
      </Button>
    </div>
  );
}

function WeightCell({
  value,
  baseline,
  disabled,
  onCommit,
}: {
  value: number;
  baseline: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const [pct, setPct] = useState(Math.round(value * 100));
  // Resync when the effective weight changes (e.g. another row's edit
  // renormalized this one) — adjust-during-render, no effect.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setPct(Math.round(value * 100));
  }

  const commit = () => {
    const next = pct / 100;
    if (Math.abs(next - value) > 0.0001) onCommit(next);
  };

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="relative w-16">
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={pct}
          disabled={disabled}
          onChange={(e) => setPct(Math.max(0, Math.min(100, Number(e.target.value))))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className="border-input h-7 w-full rounded-md border bg-transparent pr-5 text-right text-[12px] tabular-nums disabled:opacity-50"
        />
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-1.5 -translate-y-1/2 text-[10px]">
          %
        </span>
      </div>
      <span className="text-foreground/45 text-[9px]">baseline {Math.round(baseline * 100)}%</span>
    </div>
  );
}

function groupCriteria(criteria: EvaluationCriterionRow[]) {
  const map = new Map<CriterionGroup, EvaluationCriterionRow[]>();
  for (const c of criteria) {
    const bucket = map.get(c.group_key) ?? [];
    bucket.push(c);
    map.set(c.group_key, bucket);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => a.display_order - b.display_order);
  }
  return map;
}
